import React, { useState, useEffect } from 'react';
import { careersAPI } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import {
  MapPin, Clock, Briefcase, ArrowLeft, CheckCircle2, Loader2, Upload, Calendar, Euro,
} from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export default function CareersJob() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [job, setJob] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', message: '', website: '' });
  const [cv, setCv] = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    careersAPI.getJob(jobId)
      .then(res => setJob(res.data))
      .catch(() => setJob(null))
      .finally(() => setLoading(false));
  }, [jobId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim()) {
      toast.error('Bitte Vorname, Nachname und E-Mail ausfüllen'); return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v || ''));
      const refCode = new URLSearchParams(window.location.search).get('ref') || '';
      if (refCode) fd.append('ref', refCode);
      if (cv) fd.append('cv', cv);
      await careersAPI.apply(jobId, fd);
      setDone(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bewerbung konnte nicht übermittelt werden');
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return <div className="max-w-3xl mx-auto px-6 py-16 space-y-4"><Skeleton className="h-10 w-2/3" /><Skeleton className="h-64" /></div>;
  }

  if (!job) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="max-w-md w-full"><CardContent className="p-10 text-center space-y-4">
          <p className="text-muted-foreground">Diese Stelle ist nicht mehr verfügbar.</p>
          <Button onClick={() => navigate('/karriere')}>Alle Stellen ansehen</Button>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="careers-job-page">
      <header className="border-b bg-card">
        <div className="max-w-3xl mx-auto px-6 py-8">
          <button onClick={() => navigate('/karriere')} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6" data-testid="back-to-careers">
            <ArrowLeft className="h-4 w-4" /> Alle Stellen
          </button>
          <div className="flex items-center gap-2 text-primary font-heading font-bold text-sm mb-4">
            <Briefcase className="h-4 w-4" /> TeamHub Karriere
          </div>
          <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight">{job.title}</h1>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground mt-4">
            {job.location_name && <span className="inline-flex items-center gap-1"><MapPin className="h-4 w-4" /> {job.location_name}</span>}
            {job.employment_type && <span className="inline-flex items-center gap-1"><Clock className="h-4 w-4" /> {job.employment_type}</span>}
            {job.contract_type && <Badge variant="secondary">{job.contract_type}</Badge>}
            {job.salary_range && <span className="inline-flex items-center gap-1"><Euro className="h-4 w-4" /> {job.salary_range}</span>}
            {job.start_date && <span className="inline-flex items-center gap-1"><Calendar className="h-4 w-4" /> ab {job.start_date}</span>}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 space-y-8">
        {done ? (
          <Card className="border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30" data-testid="apply-success">
            <CardContent className="p-10 text-center space-y-3">
              <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-500" />
              <h2 className="font-heading font-semibold text-xl">Bewerbung übermittelt!</h2>
              <p className="text-muted-foreground">Vielen Dank. Wir haben deine Unterlagen erhalten und melden uns nach der Sichtung. Eine Eingangsbestätigung ist an deine E-Mail unterwegs.</p>
              <Button variant="outline" onClick={() => navigate('/karriere')}>Weitere Stellen ansehen</Button>
            </CardContent>
          </Card>
        ) : (
          <>
            {job.description && (
              <section><h3 className="font-semibold mb-2">Beschreibung</h3><p className="whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">{job.description}</p></section>
            )}
            {job.requirements && (
              <section><h3 className="font-semibold mb-2">Das bringst du mit</h3><p className="whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">{job.requirements}</p></section>
            )}
            {job.benefits && (
              <section><h3 className="font-semibold mb-2">Wir bieten</h3><p className="whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">{job.benefits}</p></section>
            )}

            {/* Bewerbungsformular */}
            <Card id="apply">
              <CardContent className="p-6">
                <h3 className="font-heading font-semibold text-lg mb-4">Jetzt bewerben</h3>
                <form onSubmit={submit} className="space-y-4" data-testid="apply-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label>Vorname *</Label>
                      <Input value={form.first_name} onChange={(e) => set('first_name', e.target.value)} data-testid="apply-first-name" required />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Nachname *</Label>
                      <Input value={form.last_name} onChange={(e) => set('last_name', e.target.value)} data-testid="apply-last-name" required />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label>E-Mail *</Label>
                      <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} data-testid="apply-email" required />
                    </div>
                    <div className="space-y-1.5">
                      <Label>Telefon</Label>
                      <Input value={form.phone} onChange={(e) => set('phone', e.target.value)} data-testid="apply-phone" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Anschreiben / Nachricht</Label>
                    <Textarea rows={4} value={form.message} onChange={(e) => set('message', e.target.value)} placeholder="Erzähl uns kurz etwas über dich…" data-testid="apply-message" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Lebenslauf (PDF, Word, Bild – max. 15 MB)</Label>
                    <label className="flex items-center gap-2 border border-dashed rounded-lg px-3 py-2.5 cursor-pointer hover:bg-muted/40 text-sm" data-testid="apply-cv-label">
                      <Upload className="h-4 w-4 text-muted-foreground" />
                      <span className="truncate">{cv ? cv.name : 'Datei auswählen…'}</span>
                      <input type="file" className="hidden" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                        onChange={(e) => setCv(e.target.files?.[0] || null)} data-testid="apply-cv-input" />
                    </label>
                  </div>
                  {/* Honeypot – für Menschen unsichtbar */}
                  <input type="text" value={form.website} onChange={(e) => set('website', e.target.value)}
                    className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                  <Button type="submit" className="w-full" disabled={submitting} data-testid="apply-submit">
                    {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Bewerbung absenden
                  </Button>
                  {job.contact_person && (
                    <p className="text-xs text-muted-foreground text-center">Fragen? Ansprechpartner: {job.contact_person}{job.contact_email ? ` · ${job.contact_email}` : ''}</p>
                  )}
                </form>
              </CardContent>
            </Card>
          </>
        )}
      </main>
      <footer className="border-t mt-12"><div className="max-w-3xl mx-auto px-6 py-6 text-xs text-muted-foreground">Powered by TeamHub</div></footer>
    </div>
  );
}
